/**
 * Input Manager
 *
 * Handles keyboard input with high-precision timestamps
 */

export interface InputEvent {
  key: string;
  lane: number;
  timestamp: number;
  consumed: boolean;
}

export class InputManager {
  private inputBuffer: InputEvent[] = [];
  private keyMap: Map<string, number>;
  private readonly BUFFER_MAX_AGE: number = 200; // Keep inputs for 200ms

  // Key bindings (can be customized later)
  private readonly DEFAULT_KEY_MAP: [string, number][] = [
    ['KeyD', 0],
    ['KeyF', 1],
    ['KeyJ', 2],
    ['KeyK', 3]
  ];

  constructor() {
    this.keyMap = new Map(this.DEFAULT_KEY_MAP);
    this.setupEventListeners();
    console.log('InputManager initialized');
  }

  private setupEventListeners(): void {
    window.addEventListener('keydown', this.onKeyDown.bind(this));
    window.addEventListener('keyup', this.onKeyUp.bind(this));
  }

  private onKeyDown(event: KeyboardEvent): void {
    // Prevent default browser behavior for game keys
    const lane = this.keyMap.get(event.code);
    if (lane !== undefined) {
      event.preventDefault();

      // Check if this key is already pressed (prevent key repeat)
      const alreadyPressed = this.inputBuffer.some(
        input => input.key === event.code && !input.consumed
      );

      if (!alreadyPressed) {
        this.inputBuffer.push({
          key: event.code,
          lane: lane,
          timestamp: performance.now(),
          consumed: false
        });
      }
    }
  }

  private onKeyUp(event: KeyboardEvent): void {
    // For future long note support
    const lane = this.keyMap.get(event.code);
    if (lane !== undefined) {
      event.preventDefault();
    }
  }

  /**
   * Get all unconsumed inputs within a time window
   */
  getRecentInputs(timeWindow: number = this.BUFFER_MAX_AGE): InputEvent[] {
    const now = performance.now();
    return this.inputBuffer.filter(
      input => !input.consumed && (now - input.timestamp) <= timeWindow
    );
  }

  /**
   * Get unconsumed inputs for a specific lane
   */
  getLaneInputs(lane: number, timeWindow: number = this.BUFFER_MAX_AGE): InputEvent[] {
    return this.getRecentInputs(timeWindow).filter(input => input.lane === lane);
  }

  /**
   * Mark an input as consumed (so it won't be processed again)
   */
  consumeInput(input: InputEvent): void {
    input.consumed = true;
  }

  /**
   * Clear old inputs from buffer (call this each frame)
   */
  update(): void {
    const now = performance.now();
    this.inputBuffer = this.inputBuffer.filter(
      input => (now - input.timestamp) <= this.BUFFER_MAX_AGE
    );
  }

  /**
   * Clear all inputs
   */
  clear(): void {
    this.inputBuffer = [];
  }

  /**
   * Set custom key bindings
   */
  setKeyBinding(key: string, lane: number): void {
    this.keyMap.set(key, lane);
  }

  /**
   * Get current key bindings
   */
  getKeyBindings(): Map<string, number> {
    return new Map(this.keyMap);
  }

  /**
   * Cleanup event listeners
   */
  dispose(): void {
    window.removeEventListener('keydown', this.onKeyDown.bind(this));
    window.removeEventListener('keyup', this.onKeyUp.bind(this));
    this.inputBuffer = [];
  }
}
